import { Effect, Schema } from 'effect';
import { HttpApiError } from 'effect/http-api';
import { ApiClient } from '@listening/api-contract/api-client-tag';
import {
  type DocumentInsightsFailureReason,
  type DocumentInsightsSelection,
  type EnsureDocumentInsightsRequest,
  suggestedDocumentInsightsModel,
} from '@listening/api-contract/document-insights';
import { randomUUID } from '@listening/utils/crypto';
import {
  BadRequestError,
  DocumentInsightsFailedError,
  NotFoundError,
  RateLimitedError,
  UnauthorizedError,
} from '@listening/api-contract/errors';

export const documentInsightsSelection = {
  model: suggestedDocumentInsightsModel(),
  context: 'full',
} as const satisfies typeof DocumentInsightsSelection.Type;

export const savedDocumentInsightsKey = (
  userId: string,
  audioId: string,
  selection: typeof DocumentInsightsSelection.Type,
) =>
  [
    'document-insights',
    userId,
    audioId,
    selection.model,
    selection.context,
  ] as const;

export const ensureSavedDocumentInsights = (
  input: typeof EnsureDocumentInsightsRequest.Type & { audioId: string },
) =>
  Effect.gen(function* () {
    const api = yield* ApiClient;
    return yield* api.Audio.EnsureDocumentInsights({
      params: { audioId: input.audioId },
      payload: {
        requestId: input.requestId,
        model: input.model,
        context: input.context,
        ...(input.retryRequestId
          ? { retryRequestId: input.retryRequestId }
          : {}),
      },
    });
  });

export const loadSavedDocumentInsights = (
  audioId: string,
  selection: typeof DocumentInsightsSelection.Type,
) =>
  Effect.gen(function* () {
    const api = yield* ApiClient;
    const saved = yield* api.Audio.GetSavedDocumentInsights({
      params: { audioId },
      query: selection,
    });
    if (saved) return saved;
    const requestId = yield* randomUUID;
    return yield* ensureSavedDocumentInsights({
      audioId,
      ...selection,
      requestId,
    });
  });

export const retrySavedDocumentInsights = (input: {
  audioId: string;
  retryRequestId: string;
}) =>
  Effect.flatMap(randomUUID, (requestId) =>
    ensureSavedDocumentInsights({
      ...input,
      ...documentInsightsSelection,
      requestId,
    }),
  );

export const documentKeyPoints = (value?: string | null) =>
  value
    ?.split(' | ')
    .map((point) => point.trim())
    .filter(Boolean) ?? [];

export type DocumentInsightsFailureKind =
  | 'no-text'
  | 'interrupted'
  | 'generation'
  | 'rate-limited'
  | 'not-ready'
  | 'unavailable'
  | 'sign-in'
  | 'connection';

export const documentInsightsFailureKindForReason = (
  reason: typeof DocumentInsightsFailureReason.Type | undefined,
): DocumentInsightsFailureKind =>
  reason === 'no-text' || reason === 'interrupted' ? reason : 'generation';

export const documentInsightsFailureKind = (
  error: unknown,
): DocumentInsightsFailureKind => {
  if (Schema.is(DocumentInsightsFailedError)(error))
    return documentInsightsFailureKindForReason(error.reason);
  if (Schema.is(RateLimitedError)(error)) return 'rate-limited';
  if (Schema.is(BadRequestError)(error)) return 'not-ready';
  if (Schema.is(NotFoundError)(error)) return 'unavailable';
  if (
    Schema.is(UnauthorizedError)(error) ||
    Schema.is(HttpApiError.Unauthorized)(error)
  )
    return 'sign-in';
  return 'connection';
};
